import { ExternalLink, X } from 'lucide-react'
import { useMemo } from 'react'
import { Link } from 'react-router-dom'

import { isAdmin } from '@/features/auth/permissions'
import { useSession } from '@/features/auth/use-session'
import {
  DueDateBadge,
  PageHeader,
  RelativeTime,
  StatusPill,
  UserAvatar,
} from '@/shared/components/common'
import { EmptyState, ErrorState, NoResultsState, SkeletonRows } from '@/shared/components/data'
import { Button, Segmented, Select } from '@/shared/components/ui'
import type { SegmentedOption } from '@/shared/components/ui'
import { useT } from '@/shared/i18n'
import type { SupportKind, SupportSeverity, SupportTicket } from '@/shared/types/domain'

import { useSupportApps, useSupportTickets, useUpdateSupportTicket } from './api'
import { ConnectedApps } from './components/ConnectedApps'
import { LeadersBar } from './components/LeadersBar'
import { ReportersList } from './components/ReportersList'
import { SeverityBadge } from './components/SeverityBadge'
import { DUE_DAYS, KINDS, SEVERITIES, kindLabel, severityLabel } from './severity'
import { useSupportFilters, type SupportState, type SupportView } from './use-support-filters'

/**
 * `/support` — los issues que reportan los usuarios de las apps internas.
 *
 * Cada fila es una tarea del proyecto de soporte de su app, con el semáforo de
 * severidad adelante. Lo que se decide acá es la **clasificación** (el chat la
 * propone, el equipo la corrige) y el **plazo**: un urgente vence hoy, y para
 * el resto el PM o el programador elige de 1 a 10 días. El trabajo en sí se
 * hace en la tarea, que es la misma que aparece en Mis tareas y Vencimientos.
 *
 * La vista "Por usuario" es el panel de quién reporta y cómo le fue; de una
 * fila se salta a los reportes de esa persona. La vista "Apps" es la de las
 * apps conectadas: un admin conecta una nueva desde ahí (clave, manual técnico
 * y tour de instalación). Arriba de todo, a quién le llega el aviso de cada
 * reporte (el responsable y los líderes).
 */
export function SupportPage() {
  const t = useT()
  const { me } = useSession()
  const { filters, setFilter, showReporter, clearFilters, isFiltered } = useSupportFilters()
  const enTickets = filters.view === 'tickets'
  const query = useSupportTickets(filters)
  const apps = useSupportApps()
  const update = useUpdateSupportTicket()

  const viewOptions = useMemo<readonly SegmentedOption<SupportView>[]>(
    () => [
      { value: 'tickets', label: t('support.view.tickets') },
      { value: 'users', label: t('support.view.users') },
      { value: 'apps', label: t('support.view.apps') },
    ],
    [t],
  )

  const stateOptions = useMemo<readonly SegmentedOption<SupportState>[]>(
    () => [
      { value: 'open', label: t('support.filter.state.open') },
      { value: 'done', label: t('support.filter.state.done') },
      { value: 'all', label: t('support.filter.state.all') },
    ],
    [t],
  )

  const tickets = query.data ?? []

  return (
    <div className="flex flex-col">
      <PageHeader
        title={t('nav.support')}
        description={t('support.description')}
        actions={
          <Segmented
            label={t('support.view.label')}
            value={filters.view}
            options={viewOptions}
            onChange={(value) => setFilter('view', value)}
          />
        }
      />

      <LeadersBar apps={apps.data ?? []} canEdit={isAdmin(me)} />

      {filters.view === 'users' ? (
        <div className="p-4">
          <ReportersList onSelect={showReporter} />
        </div>
      ) : null}

      {filters.view === 'apps' ? <ConnectedApps canConnect={isAdmin(me)} /> : null}

      {enTickets ? (
        <div className="hairline-b sticky top-0 z-10 flex flex-wrap items-center gap-2 bg-surface-1 px-4 py-2">
          <Segmented
            label={t('support.filter.state.label')}
            value={filters.state}
            options={stateOptions}
            onChange={(value) => setFilter('state', value)}
          />

          <Select
            aria-label={t('support.filter.severity')}
            value={filters.severity ?? ''}
            onChange={(event) =>
              setFilter('severity', (event.target.value || null) as SupportSeverity | null)
            }
            className="w-auto"
          >
            <option value="">{t('support.filter.allSeverities')}</option>
            {SEVERITIES.map((severity) => (
              <option key={severity} value={severity}>
                {severityLabel(t, severity)}
              </option>
            ))}
          </Select>

          <Select
            aria-label={t('support.filter.kind')}
            value={filters.kind ?? ''}
            onChange={(event) =>
              setFilter('kind', (event.target.value || null) as SupportKind | null)
            }
            className="w-auto"
          >
            <option value="">{t('support.filter.allKinds')}</option>
            {KINDS.map((kind) => (
              <option key={kind} value={kind}>
                {kindLabel(t, kind)}
              </option>
            ))}
          </Select>

          <Select
            aria-label={t('support.filter.app')}
            value={filters.app ?? ''}
            onChange={(event) => setFilter('app', event.target.value || null)}
            className="w-auto"
          >
            <option value="">{t('support.filter.allApps')}</option>
            {(apps.data ?? []).map((app) => (
              <option key={app.id} value={app.tool_slug}>
                {app.name}
              </option>
            ))}
          </Select>

          {filters.reporter ? (
            <Button
              variant="ghost"
              aria-label={t('support.filter.clearReporter', { quien: filters.reporter })}
              onClick={() => setFilter('reporter', null)}
            >
              {t('support.filter.reporter', { quien: filters.reporter })}
              <X aria-hidden="true" className="h-3 w-3" />
            </Button>
          ) : null}

          {isFiltered ? (
            <Button variant="ghost" onClick={clearFilters}>
              {t('common.action.clearFilters')}
            </Button>
          ) : null}
        </div>
      ) : null}

      {!enTickets ? null : query.isPending ? (
        <div className="p-4">
          <SkeletonRows rows={6} />
        </div>
      ) : query.isError ? (
        <div className="p-4">
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        </div>
      ) : tickets.length === 0 && isFiltered ? (
        <div className="p-4">
          <NoResultsState onClearFilters={clearFilters} />
        </div>
      ) : tickets.length === 0 ? (
        <div className="p-4">
          <EmptyState
            title={t('support.empty.title')}
            description={t('support.empty.description')}
          />
        </div>
      ) : (
        <ul aria-label={t('support.listLabel')}>
          {tickets.map((ticket) => (
            <TicketRow
              key={ticket.id}
              ticket={ticket}
              onPatch={(patch) => update.mutate({ id: ticket.id, patch })}
            />
          ))}
        </ul>
      )}
    </div>
  )
}

interface TicketRowProps {
  ticket: SupportTicket
  onPatch: (patch: { severity?: SupportSeverity; kind?: SupportKind; due_in_days?: number }) => void
}

function TicketRow({ ticket, onPatch }: TicketRowProps) {
  const t = useT()
  const urgent = ticket.severity === 'urgent'
  const reporter = ticket.reporter.name ?? ticket.reporter.email

  return (
    <li className="hairline-b flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2.5">
      <SeverityBadge severity={ticket.severity} className="shrink-0" />
      {ticket.resolved_by_ai ? (
        <StatusPill category="done" variant="quiet">
          {t('support.row.resolvedByAi')}
        </StatusPill>
      ) : null}

      <div className="flex min-w-[16rem] flex-1 flex-col gap-0.5">
        <Link
          to={`/projects/${ticket.project.id}/tareas`}
          className="truncate text-sm text-text-1 hover:underline"
        >
          {ticket.title}
        </Link>
        <div className="flex flex-wrap items-center gap-x-1.5 text-2xs text-text-3">
          <span className="num text-text-2">{ticket.code}</span>
          <span aria-hidden="true">·</span>
          <span className="text-text-2">{ticket.app.name}</span>
          {ticket.linkedProject ? (
            <>
              <span aria-hidden="true">·</span>
              <Link to={`/projects/${ticket.linkedProject.id}`} className="hover:underline">
                {ticket.linkedProject.name}
              </Link>
            </>
          ) : null}
          <span aria-hidden="true">·</span>
          <span>{kindLabel(t, ticket.kind)}</span>
          {reporter ? (
            <>
              <span aria-hidden="true">·</span>
              <span>{t('support.row.reportedBy', { quien: reporter })}</span>
            </>
          ) : null}
          <span aria-hidden="true">·</span>
          <RelativeTime value={ticket.created_at} className="text-2xs" />
          {ticket.source_url ? (
            <>
              <span aria-hidden="true">·</span>
              <a
                href={ticket.source_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 hover:text-text-1 hover:underline"
              >
                {t('support.row.conversation')}
                <ExternalLink aria-hidden="true" className="h-3 w-3" />
              </a>
            </>
          ) : null}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <StatusPill category={ticket.stage.category} variant="quiet">
          {ticket.stage.label}
        </StatusPill>
        <UserAvatar user={ticket.assignee} withName />
        <DueDateBadge date={ticket.due_date} isDone={ticket.completed_at !== null} />

        {/*
          El plazo se elige, no se tipea: de 1 a 10 días desde hoy. Un urgente
          vence hoy y el control lo dice en vez de desaparecer, para que nadie
          lo busque.
        */}
        <Select
          aria-label={t('support.row.dueLabel')}
          title={urgent ? t('support.row.urgentDueToday') : undefined}
          value=""
          disabled={urgent || ticket.completed_at !== null}
          onChange={(event) => {
            if (event.target.value) onPatch({ due_in_days: Number(event.target.value) })
          }}
          className="w-auto"
        >
          <option value="">
            {urgent ? t('support.row.urgentDueToday') : t('support.row.duePlaceholder')}
          </option>
          {DUE_DAYS.map((days) => (
            <option key={days} value={days}>
              {t('support.row.dueOption', { count: days })}
            </option>
          ))}
        </Select>

        <Select
          aria-label={t('support.row.severityLabel')}
          value={ticket.severity}
          onChange={(event) => onPatch({ severity: event.target.value as SupportSeverity })}
          className="w-auto"
        >
          {SEVERITIES.map((severity) => (
            <option key={severity} value={severity}>
              {severityLabel(t, severity)}
            </option>
          ))}
        </Select>

        <Select
          aria-label={t('support.row.kindLabel')}
          value={ticket.kind}
          onChange={(event) => onPatch({ kind: event.target.value as SupportKind })}
          className="w-auto"
        >
          {KINDS.map((kind) => (
            <option key={kind} value={kind}>
              {kindLabel(t, kind)}
            </option>
          ))}
        </Select>
      </div>
    </li>
  )
}
